import React, { useState, useRef, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Alert,
} from '@mui/material';
import { Security } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { mfaAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { COMPANY_NAME } from '../constants';

const MFAVerify = () => {
  const navigate = useNavigate();
  const { user, logout, setMfaVerified } = useAuth();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const codeRef = useRef(null);

  useEffect(() => {
    codeRef.current?.focus();
  }, []);

  const handleVerify = async () => {
    const trimmed = code.replace(/\s/g, '');
    if (trimmed.length !== 6 || !/^\d{6}$/.test(trimmed)) {
      setError('Please enter a 6-digit code.');
      return;
    }
    setVerifying(true);
    setError('');
    try {
      await mfaAPI.verify(trimmed);
      setMfaVerified(true);
      localStorage.setItem('mfa_verified', 'true');
      navigate('/', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || 'Verification failed.');
      setCode('');
      codeRef.current?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleVerify();
    }
  };

  const handleSignOut = () => {
    localStorage.removeItem('mfa_verified');
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center' }}>
      <Container maxWidth="xs">
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Security color="primary" sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            Two-Factor Authentication
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {COMPANY_NAME}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Open your authenticator app and enter the 6-digit code for <strong>{user?.email}</strong>
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <TextField
            inputRef={codeRef}
            label="6-digit code"
            value={code}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '').slice(0, 6);
              setCode(v);
            }}
            onKeyDown={handleKeyDown}
            fullWidth
            autoFocus
            inputProps={{
              maxLength: 6,
              style: { fontSize: '1.8rem', letterSpacing: 10, textAlign: 'center', fontFamily: 'monospace' },
              inputMode: 'numeric',
              autoComplete: 'one-time-code',
            }}
            sx={{ mb: 3 }}
          />

          <Button
            variant="contained"
            fullWidth
            onClick={handleVerify}
            disabled={verifying || code.length !== 6}
            sx={{ mb: 2 }}
          >
            {verifying ? 'Verifying...' : 'Verify'}
          </Button>

          <Button
            variant="text"
            size="small"
            color="inherit"
            onClick={handleSignOut}
          >
            Sign out and use a different account
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default MFAVerify;
